import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete, Link as LinkIcon, Refresh, StopCircle } from '@mui/icons-material';
import apiClient from '../../../services/api';
import type { WebPlayerCreate, WebPlayerCreated, WebPlayerLink } from '../../../types';
import { invalidateWebPlayerData } from '../../../utils/cacheInvalidation';

const initialForm: WebPlayerCreate = {
  name: '',
  description: '',
  location: '',
  screen_resolution: '1920x1080',
  screen_orientation: 'landscape',
};

export const WebPlayersTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<WebPlayerCreate>(initialForm);
  const [latestCreated, setLatestCreated] = useState<WebPlayerCreated | null>(null);

  const webPlayers = useQuery({
    queryKey: ['web-players'],
    queryFn: () => apiClient.getWebPlayers(1, 100),
  });

  const refreshList = () => invalidateWebPlayerData(queryClient);

  const createMutation = useMutation({
    mutationFn: (data: WebPlayerCreate) => apiClient.createWebPlayer(data),
    onSuccess: (result: WebPlayerCreated) => {
      setLatestCreated(result);
      setOpen(false);
      setForm(initialForm);
      refreshList();
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (id: number) => apiClient.rotateWebPlayerToken(id),
    onSuccess: (result: WebPlayerCreated) => {
      setLatestCreated(result);
      refreshList();
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => apiClient.revokeWebPlayer(id),
    onSuccess: refreshList,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteWebPlayer(id),
    onSuccess: refreshList,
  });

  const latestServerUrl = latestCreated ? getServerUrl(latestCreated.player_url) : '';

  const copyText = async (value: string) => {
    await navigator.clipboard.writeText(value);
  };

  const submit = () => {
    createMutation.mutate(form);
  };

  const rows: WebPlayerLink[] = webPlayers.data?.items || [];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6">Web Players</Typography>
          <Typography variant="body2" color="text.secondary">
            Браузерные клиенты с доступом по секретной ссылке.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
          Создать web-player
        </Button>
      </Stack>

      {latestCreated && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" startIcon={<ContentCopy />} onClick={() => copyText(latestServerUrl)}>
                Server URL
              </Button>
              <Button color="inherit" size="small" startIcon={<ContentCopy />} onClick={() => copyText(latestCreated.token)}>
                Token
              </Button>
              <Button color="inherit" size="small" startIcon={<ContentCopy />} onClick={() => copyText(latestCreated.player_url)}>
                Link
              </Button>
            </Stack>
          }
        >
          <Stack spacing={0.5}>
            <Typography variant="body2">Ссылка создана. Полные данные показываются только сейчас.</Typography>
            <Typography variant="body2">Android Server URL: {latestServerUrl}</Typography>
            <Typography variant="body2">Android Player token: {latestCreated.token}</Typography>
            <Typography variant="body2">Полная ссылка: {latestCreated.player_url}</Typography>
          </Stack>
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Устройство</TableCell>
              <TableCell>Токен</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Последнее использование</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.description || row.device?.location || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button size="small" href={`/devices?device=${row.device_id}`} startIcon={<LinkIcon />}>
                    #{row.device_id}
                  </Button>
                </TableCell>
                <TableCell>{row.token_prefix}...</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.is_active && !row.revoked_at ? 'active' : 'revoked'}
                    color={row.is_active && !row.revoked_at ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{row.last_used_at ? new Date(row.last_used_at).toLocaleString() : 'never'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" startIcon={<Refresh />} onClick={() => rotateMutation.mutate(row.id)}>
                      Rotate
                    </Button>
                    <Button size="small" color="warning" startIcon={<StopCircle />} onClick={() => revokeMutation.mutate(row.id)}>
                      Revoke
                    </Button>
                    <Button size="small" color="error" startIcon={<Delete />} onClick={() => deleteMutation.mutate(row.id)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    Web-player пока не созданы.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Создать web-player</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Название"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Локация"
              value={form.location || ''}
              onChange={(event) => setForm({ ...form, location: event.target.value })}
              fullWidth
            />
            <TextField
              label="Описание"
              value={form.description || ''}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Разрешение"
              value={form.screen_resolution || ''}
              onChange={(event) => setForm({ ...form, screen_resolution: event.target.value })}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={submit} disabled={!form.name || createMutation.isPending}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const getServerUrl = (playerUrl: string) => {
  try {
    const parsed = new URL(playerUrl);
    return parsed.origin;
  } catch {
    return playerUrl.replace(/\/player\/.*$/, '');
  }
};

export default WebPlayersTab;
